import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner, Badge } from "reactstrap";
import DataTable from "react-data-table-component";
import { Btn, H3 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import { Edit, Plus, Trash2 } from "react-feather";
import { useAuth } from "../../Context/AuthContext";

const ViewAccident = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");

    const fetchData = async (page = 1, size = 10, search = "", sortCol = "", sortDir = "") => {
        setLoading(true);
        try {
            const payload: any = {
                page,
                per_page: size,
                search,
            };
            if (sortCol) {
                payload.sort_by = sortCol;
                payload.sort_direction = sortDir;
            }
            const response = await apiPost("accidents/getall", payload);
            setData(response.data);
            setTotalRows(response.total);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching accidents:", error);
            toast.error("Failed to fetch accidents");
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
    }, [currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };
    const handleSort = (column: any, sortDir: string) => {
        setSortColumn(column.sortField || "");
        setSortDirection(sortDir);
    };

    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_accident`, { state: { id } });

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await apiPost("accidents/delete", { id });
                toast.success("Record deleted successfully");
                fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
            } catch (error) {
                console.error("Error deleting record:", error);
                toast.error("Failed to delete record");
            }
        }
    };

    const columns = [
        {
            name: "Sl. No",
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        { name: "Vehicle", selector: (row: any) => row.vehicle?.registration_number || "N/A", sortable: true, sortField: "vehicle_id", wrap: true },
        { name: "Driver", selector: (row: any) => row.driver?.name || "N/A", sortable: true, sortField: "driver_id", wrap: true },
        {
            name: "Incident Date",
            selector: (row: any) => row.incident_date_time,
            cell: (row: any) => row.incident_date_time ? new Date(row.incident_date_time).toLocaleDateString('en-GB') : '',
            sortable: true,
            sortField: "incident_date_time",
            wrap: true
        },
        { name: "Severity", selector: (row: any) => row.incident_severity, sortable: true, sortField: "incident_severity", wrap: true },
        { name: "Location", selector: (row: any) => row.incident_location, sortable: true, sortField: "incident_location", wrap: true },
        { name: "Total Amount", selector: (row: any) => row.actual_repair_cost ? Number(row.actual_repair_cost).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00', sortable: true, sortField: "actual_repair_cost", wrap: true },
        {
            name: "Status",
            selector: (row: any) => row.accident_status,
            sortable: true,
            sortField: "accident_status",
            wrap: true,
            cell: (row: any) => (
                <Badge color={
                    row.accident_status === 'Closed' ? 'success' :
                        row.accident_status === 'In Progress' ? 'warning' :
                            row.accident_status === 'Open' ? 'danger' : 'secondary'
                } pill>
                    {row.accident_status}
                </Badge>
            )
        },
        {
            name: "Action",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">

                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('accident.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];


    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };
    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Accidents" parent="Safety" title="Accidents List" />
            <Container fluid>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>

                                <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                    <H3 className="text-primary">Accident Overview</H3>
                                    <Btn color="danger" onClick={() => navigate(`${process.env.PUBLIC_URL}/add_accident`)}>
                                        <Plus size={16} /> Add Accident
                                    </Btn>
                                </Col>
                                {hasPermission('accident.view') && (
                                    <>
                                        <Col xs={12} className="mb-3 d-flex justify-content-end">
                                            <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                        </Col>
                                        <DataTable
                                            columns={columns}
                                            data={data}
                                            progressPending={loading}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onChangePage={handlePageChange}
                                            sortServer
                                            onSort={handleSort}
                                            highlightOnHover
                                            striped
                                            responsive
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            customStyles={customStyles}
                                        />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default ViewAccident;
